/**
 * RecentCorpActionsPanel — Round P118g (Feb 2026)
 *
 * Lists every stock split / spinoff that DiviDrip auto-applied
 * during a recent CSV/XLSX import, with a per-row Undo button.
 * Rendered inside the Portfolio Settings area (see Dashboard.js).
 *
 * Backend contract:
 *   GET  /api/portfolio/corp-actions                   → { corp_actions: [...] }
 *   POST /api/portfolio/corp-actions/{id}/undo         → { ok, lots_modified, spinoff_lots_removed }
 *
 * A "recent" action = any row without `undone_at` set.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Undo2, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RecentCorpActionsPanel({ portfolioId, onUndone }) {
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchList = () => {
    setLoading(true);
    const params = portfolioId ? { portfolio_id: portfolioId } : {};
    axios.get(`${API}/api/portfolio/corp-actions`, { params, withCredentials: true })
      .then((r) => setRows(Array.isArray(r.data?.corp_actions) ? r.data.corp_actions : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchList();
  }, [portfolioId]); // eslint-disable-line

  const undo = async (id, label) => {
    if (busyId) return;
    setBusyId(id);
    try {
      const { data } = await axios.post(
        `${API}/api/portfolio/corp-actions/${id}/undo`,
        {},
        { withCredentials: true },
      );
      toast.success(`Reverted ${label}`, {
        description: `${data.lots_modified || 0} lot(s) restored${
          data.spinoff_lots_removed ? `, ${data.spinoff_lots_removed} spinoff lot(s) removed` : ''
        }.`,
      });
      fetchList();
      onUndone?.();
    } catch (err) {
      toast.error('Undo failed', { description: err?.response?.data?.detail || String(err) });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <div
      className="rounded-md border border-violet-400/50 bg-violet-500/10 p-3 space-y-2"
      data-testid="recent-corp-actions"
    >
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-violet-800 dark:text-violet-200">
        <Sparkles className="w-3.5 h-3.5" />
        Recent Corporate Actions ({rows.length})
      </div>
      <ul className="space-y-1.5">
        {rows.map((ca) => {
          const label = `${ca.ticker || ''} ${ca.type}${ca.ratio ? ` (${ca.ratio}×)` : ''}`;
          return (
            <li
              key={ca.corp_action_id}
              className="flex items-center justify-between gap-2 text-[11px] rounded bg-background/60 px-2 py-1.5"
            >
              <div className="min-w-0 flex-1">
                <div className="font-mono truncate">
                  <span className="text-muted-foreground">{ca.date} · </span>
                  {ca.parent_ticker ? `${ca.parent_ticker} → ` : ''}
                  <span className="font-bold">{ca.ticker}</span>
                  {' '}{ca.type}
                  {ca.ratio ? <span className="ml-1 text-violet-700 dark:text-violet-300">({ca.ratio}×)</span> : null}
                </div>
              </div>
              <button
                type="button"
                onClick={() => undo(ca.corp_action_id, label)}
                disabled={busyId === ca.corp_action_id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border border-rose-400/50 text-rose-700 dark:text-rose-300 hover:bg-rose-500/15 disabled:opacity-50"
                data-testid={`corp-action-undo-${ca.corp_action_id}`}
                title="Reverse this split/spinoff on your portfolio lots."
              >
                <Undo2 className="w-3 h-3" />
                {busyId === ca.corp_action_id ? 'Reverting…' : 'Undo'}
              </button>
            </li>
          );
        })}
      </ul>
      <p className="text-[10px] text-violet-800/70 dark:text-violet-200/70 italic">
        Undo restores pre-split share counts &amp; cost/share (for splits) or removes
        spinoff lots &amp; restores parent basis (for spinoffs). Safe to re-run — nothing
        else in the portfolio is touched.
      </p>
    </div>
  );
}
